import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ name, email, password, company_name: companyName });
      toast.success("Bienvenue !");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de créer le compte");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-cyan-400 to-emerald-500 flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-slate-950" />
          </div>
          <span className="font-display font-bold tracking-tight">CLEANING CONTRACT AI</span>
        </Link>
        <Card className="p-8 bg-card border-border">
          <h1 className="font-display text-2xl font-bold mb-2">Créer un compte</h1>
          <p className="text-sm text-muted-foreground mb-6">Commencez à trouver des contrats en 60 secondes</p>

          <Button
            type="button"
            variant="outline"
            className="w-full mb-4 border-border"
            onClick={handleGoogle}
            data-testid="google-signup-button"
          >
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continuer avec Google
          </Button>

          <div className="flex items-center gap-3 my-4 text-xs text-muted-foreground">
            <div className="flex-1 h-px bg-border" /> OU <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nom complet</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="company">Nom de l'entreprise</Label>
              <Input id="company" value={companyName} onChange={(e) => setCompanyName(e.target.value)} data-testid="register-company-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">Courriel</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password-input" className="mt-1.5" />
            </div>
            <Button type="submit" className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold" disabled={loading} data-testid="register-submit-button">
              {loading ? "Création…" : "Créer mon compte"}
            </Button>
          </form>

          <div className="text-sm text-center text-muted-foreground mt-6">
            Déjà un compte ?{" "}
            <Link to="/login" className="text-cyan-400 hover:underline" data-testid="login-link">Se connecter</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
